'use client'
import React, { useEffect, useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import Image from 'next/image'
import Link from 'next/link'
import { AnimatePresence, motion } from 'framer-motion'
import { GraduationCap, Download } from 'lucide-react'
import { QuillContentRenderer } from '@/components/ui/quill-content-renderer'
import { FaSquareXTwitter } from "react-icons/fa6";
import { MdQuiz } from "react-icons/md";

import {
  FaBook,
  FaVideo,
  FaClipboardCheck,
  FaMobileAlt,
  FaUsers,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaSchool,
  FaBookOpen,
  FaCalculator,
  FaFlask,
  FaGlobe,
  FaStar,
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaGooglePlay,
  FaApple,
  FaChevronLeft,
  FaClock,
  FaUser,
  FaChevronRight
} from 'react-icons/fa'
import { coursesApi } from '@/lib/api/courses'

export default function Homepage() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [courses, setCourses] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState('home')

  const [grandTests, setGrandTests] = useState<any[]>([])
  const [loadingGrandTests, setLoadingGrandTests] = useState(true)

  // Fetch courses for homepage
  const fetchHomepageCourses = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await coursesApi.getHomepageCourses()
      console.log('📚 Login Courses - Response:', response);

      if (response.success) {
        let coursesData: any[] = [];
        const data = response.data as any;

        if (Array.isArray(data)) {
          coursesData = data;
        } else if (data?.data && Array.isArray(data.data)) {
          coursesData = data.data;
        } else if (data?.data?.data && Array.isArray(data.data.data)) {
          coursesData = data.data.data;
        }

        setCourses(coursesData);
      } else {
        setError('Failed to fetch courses')
      }
    } catch (err) {
      console.error('Error fetching homepage courses:', err)
      setError('Error loading courses')
    } finally {
      setLoading(false)
    }
  }

  const fetchHomepageGrandTests = async () => {
    try {
      setLoadingGrandTests(true)
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api/v1';
      let response = await fetch(`${apiUrl}/public/grand-tests/homepage`);
      if (!response.ok) {
        response = await fetch(`${apiUrl}/grand-tests/public`);
      }
      if (response.ok) {
        const resData = await response.json();
        const list = resData.data?.data || resData.data || resData || [];
        setGrandTests(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Error fetching homepage grand tests:', err);
    } finally {
      setLoadingGrandTests(false);
    }
  }

  // Fetch courses & grand tests on component mount
  useEffect(() => {
    fetchHomepageCourses()
    fetchHomepageGrandTests()
  }, [])
  const testimonials = [
    {
      name: 'Ravi Kumar',
      text: 'SCOREEZE made learning so much fun! The quizzes and revision notes helped me score better.',
      rating: 5,
      avatar: 'https://img.freepik.com/premium-photo/cute-little-indian-indian-asian-school-boy-wearing-uniform_54391-1877.jpg?w=2000'
    },
    {
      name: 'Sita Reddy',
      text: 'I love the interactive videos. They make understanding concepts very easy.',
      rating: 4,
      avatar: 'https://thfvnext.bing.com/th/id/OIP.DBCy3SIVpC__FQD8poVQygHaEJ?w=315&h=180&c=7&r=0&o=7&cb=thfvnext&dpr=1.3&pid=1.7&rm=3'
    },
    {
      name: 'Arjun Das',
      text: 'The platform helped me revise effectively. The assessments were very useful.',
      rating: 5,
      avatar: 'https://thfvnext.bing.com/th/id/OIP.GC9RckV-8EYPWt9ddVyirQHaHa?w=170&h=180&c=7&r=0&o=7&cb=thfvnext&dpr=1.3&pid=1.7&rm=3'
    },
    {
      name: 'Anita Sharma',
      text: 'SCOREEZE is amazing! I can study at my own pace and track my progress easily.',
      rating: 5,
      avatar: 'https://thfvnext.bing.com/th/id/OIP.Ws-XMowe5IxGMy0fQJh8kAHaEJ?w=333&h=187&c=7&r=0&o=7&cb=thfvnext&dpr=1.3&pid=1.7&rm=3'
    }
  ]
  const [index, setIndex] = useState(0)
  const [cardsPerView, setCardsPerView] = useState(2)

  // Smooth scroll function with offset for navbar
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const navbarHeight = 80 // Approximate navbar height
      const elementPosition = element.offsetTop - navbarHeight - 20 // 20px additional gap
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      })
    }
  }

  // update cardsPerView on screen resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCardsPerView(1) // mobile → 1 card
      } else {
        setCardsPerView(2) // desktop/tablet → 2 cards
      }
    }

    handleResize() // run once on mount
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Handle URL hash on page load for navigation from other pages
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash.substring(1) // Remove the '#'
      if (hash) {
        // Small delay to ensure page is fully loaded
        setTimeout(() => {
          scrollToSection(hash)
        }, 100)
      }
    }

    handleHashNavigation()
  }, [])

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about-scoreeze', 'courses', 'grandtests', 'why-choose-us']
      let currentSection = 'home'

      const scrollPosition = window.scrollY + 100 // Offset for navbar

      for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const offsetTop = element.offsetTop
          const offsetBottom = offsetTop + element.offsetHeight

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            currentSection = section
          }
        }
      }

      setActiveSection(currentSection)
    }

    window.addEventListener('scroll', handleScroll)
    // Call once to set initial state
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const next = () => {
    setIndex(prev =>
      prev + cardsPerView < testimonials.length ? prev + cardsPerView : 0
    )
  }

  const prev = () => {
    setIndex(prev =>
      prev - cardsPerView >= 0
        ? prev - cardsPerView
        : testimonials.length - cardsPerView
    )
  }

  // pick cards depending on screen
  const visibleTestimonials = testimonials.slice(index, index + cardsPerView)


  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 custom-font'>
      {/* Navbar */}
      <nav className=' px-8 py-4 shadow-sm bg-white sticky top-0 z-50'>
        <div className='max-w-6xl mx-auto flex items-center justify-between'>
          <div className='flex items-center'>
            <Image
              src='/ScoreEZE_Logo.png'
              alt='SCOREEZE Logo'
              width={120}
              height={40}
              className='h-16 w-auto'
            />
          </div>
          <ul className='hidden md:flex items-center space-x-8 font-medium'>
            <li className={`cursor-pointer transition-colors ${activeSection === 'home' ? 'text-cyan-600 font-bold border-b-2 border-cyan-600 pb-1' : 'text-gray-700 hover:text-cyan-600'}`} onClick={() => scrollToSection('home')}>Home</li>
            <li className={`cursor-pointer transition-colors ${activeSection === 'about-scoreeze' ? 'text-cyan-600 font-bold border-b-2 border-cyan-600 pb-1' : 'text-gray-700 hover:text-cyan-600'}`} onClick={() => scrollToSection('about-scoreeze')}>About</li>
            <li className={`cursor-pointer transition-colors ${activeSection === 'courses' ? 'text-cyan-600 font-bold border-b-2 border-cyan-600 pb-1' : 'text-gray-700 hover:text-cyan-600'}`} onClick={() => scrollToSection('courses')}>Courses</li>
            <li className={`cursor-pointer transition-colors ${activeSection === 'grandtests' ? 'text-cyan-600 font-bold border-b-2 border-cyan-600 pb-1' : 'text-gray-700 hover:text-cyan-600'}`} onClick={() => scrollToSection('grandtests')}>Grand Tests</li>
            <li className={`cursor-pointer transition-colors ${activeSection === 'why-choose-us' ? 'text-cyan-600 font-bold border-b-2 border-cyan-600 pb-1' : 'text-gray-700 hover:text-cyan-600'}`} onClick={() => scrollToSection('why-choose-us')}>Why Us?</li>
          </ul>
          <div className='flex items-center gap-3'>
            <Link href='/login' className='bg-cyan-600 text-white hover:bg-cyan-700 px-4 py-2 rounded-sm' >
              Get Started
            </Link>
            {/* <Button className='bg-cyan-600 text-white hover:bg-cyan-700'>
              Get Started
            </Button> */}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id='home' className='relative overflow-hidden bg-gradient-to-tr from-cyan-100 via-white to-emerald-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center'>
            {/* Left Side Content */}
            <div className='flex flex-col justify-center max-w-2xl mx-auto lg:mx-0 space-y-6 sm:space-y-8 lg:space-y-12 z-10 relative text-center lg:text-left'>
              {/* Animated Heading */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                className='relative'
              >
                <div className='absolute -left-3 sm:-left-5 top-2 sm:top-3 w-1 h-12 sm:h-16 bg-cyan-500 rounded hidden lg:block'></div>
                <motion.h1 className='text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight pl-0 lg:pl-4 lg:leading-[50px] xl:leading-[60px]'>
                  Making{' '}
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 1 }}
                    className='text-cyan-600'
                  >
                    Learning
                  </motion.span>{' '}
                  <br className='hidden sm:block' />
                  <motion.span
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 1, duration: 1 }}
                    className='text-green-600'
                  >
                    Smarter
                  </motion.span>{' '}
                  &amp;{' '}
                  <motion.span
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 1.3, duration: 1 }}
                    className='text-emerald-600'
                  >
                    Affordable
                  </motion.span>
                </motion.h1>
              </motion.div>

              {/* Supporting Paragraph */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                className='text-base sm:text-lg text-gray-700 max-w-4xl mx-auto lg:mx-0'
              >
                Personalized learning designed for enthusiastic learners. Explore our vast educational content ranging from course materials & video classes to practice & self-assessment tests. These resources offer a lifetime gain.

              </motion.p>

              {/* Download Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.5, duration: 1 }}
                className='flex justify-center lg:justify-start'
              >
                <button
                  onClick={() => setIsModalOpen(true)}
                  className='inline-block bg-cyan-600 text-white px-6 sm:px-8 py-3 rounded-lg shadow-lg hover:bg-cyan-700 font-semibold text-base sm:text-lg transition-colors'
                >
                  Download Our App
                </button>
              </motion.div>
            </div>

            {/* Right Side */}
            <div className='relative flex justify-center items-center z-10 mt-8 lg:mt-0'>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8 }}
                className='absolute w-64 h-64 sm:w-80 sm:h-80 lg:w-full lg:h-full rounded-3xl overflow-hidden'
              >
                <Image
                  src='/bg-removebg-preview.png'
                  alt='Student studying'
                  fill
                  className='object-cover'
                />
              </motion.div>

              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8 }}
                className='relative w-64 h-64 sm:w-80 sm:h-80 lg:w-[420px] lg:h-[420px] rounded-3xl overflow-hidden z-10 mr-0 lg:mr-[80px]'
              >
                <Image
                  src='/hero1.png'
                  alt='Student with laptop'
                  fill
                  className='object-cover'
                />
              </motion.div>
            </div>
          </div>
        </div>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1, 0.8, 1] }}
          transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
          className='absolute top-4 left-4 sm:top-10 sm:left-10 w-6 h-6 sm:w-10 sm:h-10 bg-cyan-300 rounded-full opacity-50'
        ></motion.div>{' '}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1, 0.8, 1] }}
          transition={{ repeat: Infinity, duration: 8, ease: 'easeInOut' }}
          className='absolute bottom-8 right-8 sm:bottom-20 sm:right-20 w-8 h-8 sm:w-16 sm:h-16 bg-blue-300 rounded-full opacity-40'
        ></motion.div>{' '}
        {/* Animated Triangles */}{' '}
        <motion.svg
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 12, ease: 'linear' }}
          className='absolute top-1/4 right-8 sm:right-16 lg:right-48 w-8 h-8 sm:w-12 sm:h-12 opacity-40'
          viewBox='0 0 100 100'
        >
          {' '}
          <polygon points='50,0 100,100 0,100' fill='#34d399' />{' '}
        </motion.svg>{' '}
        {/* {/* Animated Blobs */}{' '}
        <motion.div
          initial={{ scale: 0.7, opacity: 0.3 }}
          animate={{ scale: [0.7, 1, 0.9, 1], opacity: [0.3, 0.5, 0.4, 0.5] }}
          transition={{ repeat: Infinity, duration: 8, ease: 'easeInOut' }}
          className='absolute top-1/3 right-0 w-32 h-32 sm:w-48 sm:h-48 bg-purple-300 rounded-full blur-3xl opacity-40'
        ></motion.div>{' '}
        <motion.div
          initial={{ scale: 0.7, opacity: 0.2 }}
          animate={{ scale: [0.8, 1, 0.9, 1], opacity: [0.2, 0.4, 0.3, 0.4] }}
          transition={{ repeat: Infinity, duration: 10, ease: 'easeInOut' }}
          className='absolute bottom-1/3 left-0 w-40 h-40 sm:w-64 sm:h-64 bg-pink-300 rounded-full blur-3xl opacity-30'
        ></motion.div>
        {/* Modal */}
        {isModalOpen && (
          <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm'>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className='bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-3xl p-8 w-full max-w-md mx-4 relative shadow-2xl border border-gray-200'
            >
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className='absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-all duration-200 text-lg font-medium'
              >
                ×
              </button>

              {/* Header */}
              <div className='text-center mb-6'>
                <div className='w-16 h-12 sm:w-20 sm:h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg border border-gray-200'>
                  <Image
                    src='/ScoreEZE_Logo.png'
                    alt='SCOREEZE Logo'
                    width={80}
                    height={24}
                    className='h-8 sm:h-12 w-auto'
                  />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 mb-2'>
                  Download SCOREEZE App
                </h2>
                <p className='text-gray-600 text-sm leading-relaxed'>
                  Get unlimited access to interactive learning content, quizzes, and personalized study materials
                </p>
              </div>

              {/* App Store Buttons */}
              <div className='space-y-3'>
                <a
                  href='https://play.google.com/store/apps/details?id=com.scoreeze'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='flex items-center justify-center w-full bg-black text-white py-4 px-6 rounded-2xl hover:bg-gray-900 transition-all duration-300 gap-3 font-semibold text-base shadow-lg hover:shadow-xl transform hover:scale-105 group'
                >
                  <FaGooglePlay className='text-xl' />
                  <div className='text-left'>
                    <div className='text-xs opacity-80'>Get it on</div>
                    <div className='text-base'>Google Play</div>
                  </div>
                  <FaChevronRight className='text-sm ml-auto opacity-60 group-hover:opacity-100 transition-opacity' />
                </a>

                <a
                  href='https://apps.apple.com/app/scoreeze/id123456789'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='flex items-center justify-center w-full bg-black text-white py-4 px-6 rounded-2xl hover:bg-gray-900 transition-all duration-300 gap-3 font-semibold text-base shadow-lg hover:shadow-xl transform hover:scale-105 group'
                >
                  <FaApple className='text-xl' />
                  <div className='text-left'>
                    <div className='text-xs opacity-80'>Download on the</div>
                    <div className='text-base'>App Store</div>
                  </div>
                  <FaChevronRight className='text-sm ml-auto opacity-60 group-hover:opacity-100 transition-opacity' />
                </a>
              </div>

              {/* Features */}
              <div className='mt-6 pt-6 border-t border-gray-200'>
                <div className='grid grid-cols-2 gap-4 text-center'>
                  <div className='flex flex-col items-center'>
                    <div className='w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mb-2'>
                      <FaVideo className='text-orange-600 text-sm' />
                    </div>
                    <span className='text-xs text-gray-600 font-medium'>Interactive Videos</span>
                  </div>
                  <div className='flex flex-col items-center'>
                    <div className='w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-2'>
                      <FaClipboardCheck className='text-blue-600 text-sm' />
                    </div>
                    <span className='text-xs text-gray-600 font-medium'>Practice Quizzes</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className='mt-6 text-center'>
                <p className='text-xs text-gray-500'>
                  Available on iOS and Android devices
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </section>



      <section id='courses' className='bg-white py-20'>
        <div className='max-w-7xl mx-auto px-6 text-center'>
          {/* Section Heading */}
          <motion.h2
            className='text-4xl md:text-5xl font-extrabold text-orange-600 mb-6'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            Courses Offered
          </motion.h2>
          <motion.p
            className='text-gray-700 text-lg max-w-2xl mx-auto mb-12 leading-relaxed'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            Explore our structured courses for students from 6th to 10th standard. Each standard has tailored content and pricing to suit learning needs.

          </motion.p>

          {/* Courses Grid */}
          {loading ? (
            <div className='flex justify-center items-center py-12'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600'></div>
            </div>
          ) : error ? (
            <div className='text-center py-12'>
              <p className='text-red-600 text-lg'>{error}</p>
            </div>
          ) : !Array.isArray(courses) || courses.length === 0 ? (
            <div className='text-center py-12'>
              <p className='text-gray-600 text-lg'>No courses available at the moment.</p>
            </div>
          ) : (
            <div className='relative group px-12'>
              {/* Carousel Buttons */}
              <button
                onClick={() => {
                  const el = document.getElementById('courses-container');
                  if (el && el.firstElementChild) {
                    const cardWidth = (el.firstElementChild as HTMLElement).offsetWidth + 32; // Add gap-8 width
                    el.scrollBy({ left: -cardWidth, behavior: 'smooth' });
                  }
                }}
                className='absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-orange-600 text-white rounded-full p-3 shadow-lg hover:bg-orange-700 transition-all opacity-100'
                aria-label="Previous courses"
              >
                <FaChevronLeft className='w-6 h-6' />
              </button>

              <div
                id="courses-container"
                className='flex overflow-x-auto gap-8 pb-8 no-scrollbar scroll-smooth'
                style={{
                  scrollSnapType: 'x mandatory',
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none'
                }}
              >
                {courses.map((course, index) => (
                  <div
                    key={course.id}
                    className='flex-none w-full md:w-[calc(33.333%-1.35rem)]'
                    style={{ scrollSnapAlign: 'start' }}
                  >
                    <motion.div
                      className='h-full bg-gradient-to-tr from-cyan-50 via-white to-white rounded-3xl shadow-lg p-8 flex flex-col gap-4 hover:scale-105 transition mx-2'
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <div className='flex items-center gap-3 mb-2'>
                        {course.thumbnail && (
                          <img
                            src={course.thumbnail}
                            alt={course.title}
                            className='w-12 h-12 rounded-lg object-cover'
                          />
                        )}
                        <h3 className='text-2xl font-bold text-orange-600'>
                          {course.title}
                        </h3>
                      </div>

                      {course.description && (
                        <div className='text-gray-700 text-sm mb-4 line-clamp-3 h-15'>
                          <QuillContentRenderer
                            content={course.description}
                            className='prose prose-sm max-w-none'
                          />
                        </div>
                      )}

                      <div className='space-y-2 mb-4'>
                        {course.level && (
                          <div className='flex items-center text-gray-700'>
                            <FaBookOpen className='inline mr-2 text-orange-500' />
                            <span className='text-sm'>Level: {course.level}</span>
                          </div>
                        )}
                        {course.instructor && (
                          <div className='flex items-center text-gray-700'>
                            <FaUser className='inline mr-2 text-orange-500' />
                            <span className='text-sm truncate'>By: {course.instructor.firstName} {course.instructor.lastName}</span>
                          </div>
                        )}
                      </div>

                      <div className='mt-auto pt-4'>
                        <div className='flex items-center justify-between mb-4'>
                          <div className='flex flex-col'>
                            {course.originalPrice && course.originalPrice > course.price ? (
                              <>
                                <span className='text-lg font-bold text-orange-600'>
                                  ₹{course.price}
                                </span>
                                <span className='text-sm text-gray-500 line-through'>
                                  ₹{course.originalPrice}
                                </span>
                              </>
                            ) : (
                              <span className='text-lg font-bold text-orange-600'>
                                ₹{course.price}
                              </span>
                            )}
                          </div>
                          {course.category && (
                            <span className='text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full truncate max-w-[100px]'>
                              {course.category.name}
                            </span>
                          )}
                        </div>

                        <button
                          onClick={() => {
                            window.location.href = `/login?courseId=${course.id}`;
                          }}
                          className='mt-4 inline-block w-full text-center px-6 py-3 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition cursor-pointer'
                        >
                          Enroll Now
                        </button>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  const el = document.getElementById('courses-container');
                  if (el && el.firstElementChild) {
                    const cardWidth = (el.firstElementChild as HTMLElement).offsetWidth + 32; // Add gap-8 width
                    el.scrollBy({ left: cardWidth, behavior: 'smooth' });
                  }
                }}
                className='absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-orange-600 text-white rounded-full p-3 shadow-lg hover:bg-orange-700 transition-all opacity-100'
                aria-label="Next courses"
              >
                <FaChevronRight className='w-6 h-6' />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Grand Tests Offered Section */}
      <section id='grandtests' className='bg-slate-50 py-20 border-t border-b border-gray-100'>
        <div className='max-w-7xl mx-auto px-6 text-center'>
          {/* Section Heading */}
          <motion.h2
            className='text-4xl md:text-5xl font-extrabold text-cyan-600 mb-6'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            Grand Tests Offered
          </motion.h2>
          <motion.p
            className='text-gray-700 text-lg max-w-2xl mx-auto mb-12 leading-relaxed'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            Practice real exam patterns with our structured Grand Tests. Master time management, section rules, and dynamic chapter blueprints.
          </motion.p>

          {/* Grand Tests Grid / Carousel */}
          {loadingGrandTests ? (
            <div className='flex justify-center items-center py-12'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600'></div>
            </div>
          ) : !Array.isArray(grandTests) || grandTests.length === 0 ? (
            <div className='text-center py-12'>
              <p className='text-gray-600 text-lg'>No grand tests available at the moment.</p>
            </div>
          ) : (
            <div className='relative group px-12'>
              {/* Carousel Navigation Buttons */}
              <button
                onClick={() => {
                  const el = document.getElementById('grand-tests-container');
                  if (el && el.firstElementChild) {
                    const cardWidth = (el.firstElementChild as HTMLElement).offsetWidth + 32;
                    el.scrollBy({ left: -cardWidth, behavior: 'smooth' });
                  }
                }}
                className='absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-cyan-600 text-white rounded-full p-3 shadow-lg hover:bg-cyan-700 transition-all'
                aria-label="Previous grand tests"
              >
                <FaChevronLeft className='w-6 h-6' />
              </button>

              <div
                id="grand-tests-container"
                className='flex overflow-x-auto gap-8 pb-8 no-scrollbar scroll-smooth'
                style={{
                  scrollSnapType: 'x mandatory',
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none'
                }}
              >
                {grandTests.map((gt: any, index: number) => {
                  const isFixed = (gt._count?.questions || 0) > 0;
                  return (
                    <div
                      key={gt.id}
                      className='flex-none w-full md:w-[calc(33.333%-1.35rem)] text-left'
                      style={{ scrollSnapAlign: 'start' }}
                    >
                      <motion.div
                        className='h-full bg-gradient-to-tr from-cyan-50/50 via-white to-white rounded-3xl shadow-lg p-8 flex flex-col gap-4 hover:scale-105 transition border border-cyan-100 mx-2'
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <div className='flex items-center justify-between gap-2 mb-1'>
                          <span className='px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-extrabold uppercase rounded-full tracking-wider'>
                            {isFixed ? 'Predefined Exam' : 'Practice Exam'}
                          </span>
                          <span className='text-xs font-bold text-gray-500 capitalize'>
                            Mode: {gt.timerType}
                          </span>
                        </div>

                        <h3 className='text-2xl font-bold text-gray-900 line-clamp-2'>
                          {gt.title}
                        </h3>

                        {gt.description && (
                          <p className='text-gray-600 text-sm line-clamp-2 leading-relaxed'>
                            {gt.description}
                          </p>
                        )}

                        <div className='space-y-2.5 my-2 border-t border-b border-gray-100 py-3 text-xs text-gray-700 font-medium'>
                          <div className='flex items-center justify-between'>
                            <span className='text-gray-500'>Duration:</span>
                            <span className='font-bold text-gray-900'>{gt.duration} Mins</span>
                          </div>
                          <div className='flex items-center justify-between'>
                            <span className='text-gray-500'>Format:</span>
                            <span className='font-bold text-cyan-600'>{gt.sections?.length || 0} Sections</span>
                          </div>
                          <div className='flex items-center justify-between'>
                            <span className='text-gray-500'>Type:</span>
                            <span className='font-bold text-gray-800'>
                              {isFixed ? `${gt._count?.questions || 0} Fixed Qs` : 'Dynamic Chapter Draw'}
                            </span>
                          </div>
                        </div>

                        <div className='mt-auto pt-2 flex items-center justify-between'>
                          <div>
                            <span className='text-xs text-gray-400 block font-medium'>Price</span>
                            <span className='text-2xl font-black text-cyan-600'>
                              {gt.price && gt.price > 0 ? `₹${gt.price}` : 'Free'}
                            </span>
                          </div>

                          <button
                            onClick={() => {
                              window.location.href = `/login?grandTestId=${gt.id}`;
                            }}
                            className='bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2.5 px-6 rounded-xl transition text-xs shadow-md shadow-cyan-200'
                          >
                            {gt.price && gt.price > 0 ? 'Enroll Test' : 'Start Practice'}
                          </button>
                        </div>
                      </motion.div>
                    </div>
                  );
                })}
              </div>

              {/* Next Carousel Button */}
              <button
                onClick={() => {
                  const el = document.getElementById('grand-tests-container');
                  if (el && el.firstElementChild) {
                    const cardWidth = (el.firstElementChild as HTMLElement).offsetWidth + 32;
                    el.scrollBy({ left: cardWidth, behavior: 'smooth' });
                  }
                }}
                className='absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-cyan-600 text-white rounded-full p-3 shadow-lg hover:bg-cyan-700 transition-all'
                aria-label="Next grand tests"
              >
                <FaChevronRight className='w-6 h-6' />
              </button>
            </div>
          )}
        </div>
      </section>

      <section id='about-scoreeze' className='bg-white py-12 sm:py-16 lg:py-20'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          {/* Mobile: Stack vertically, Desktop: Side by side */}
          <div className='flex flex-col lg:flex-row items-center gap-8 lg:gap-16'>

            {/* Left Content - Image + Stats */}
            <div className='flex-1 relative w-full lg:w-auto'>
              {/* Mobile: Show stats in a grid below image */}
              <div className='block lg:hidden mb-8'>
                <div className='grid grid-cols-2 gap-4'>
                  <motion.div
                    className='bg-white rounded-xl shadow-lg p-4 flex flex-col items-center gap-2'
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0 }}
                  >
                    <FaUsers className='text-orange-500 text-2xl' />
                    <h3 className='font-bold text-lg'>10,000+</h3>
                    <p className='text-gray-600 text-center text-xs'>Students Learned</p>
                  </motion.div>

                  <motion.div
                    className='bg-white rounded-xl shadow-lg p-4 flex flex-col items-center gap-2'
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <FaVideo className='text-orange-500 text-2xl' />
                    <h3 className='font-bold text-lg'>500+</h3>
                    <p className='text-gray-600 text-center text-xs'>Videos</p>
                  </motion.div>

                  <motion.div
                    className='bg-white rounded-xl shadow-lg p-4 flex flex-col items-center gap-2'
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <FaBook className='text-orange-500 text-2xl' />
                    <h3 className='font-bold text-lg'>100+</h3>
                    <p className='text-gray-600 text-center text-xs'>Quizzes</p>
                  </motion.div>

                  <motion.div
                    className='bg-white rounded-xl shadow-lg p-4 flex flex-col items-center gap-2'
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <FaClipboardCheck className='text-orange-500 text-2xl' />
                    <h3 className='font-bold text-lg'>150+</h3>
                    <p className='text-gray-600 text-center text-xs'>Assessments</p>
                  </motion.div>
                </div>
              </div>

              {/* Image Container */}
              <div className='relative h-[300px] sm:h-[400px] lg:h-[500px] xl:h-[600px] flex justify-center items-center'>
                {/* Background Blob */}
                <motion.div
                  className='absolute -top-6 -left-6 sm:-top-10 sm:-left-10 w-12 h-12 sm:w-16 sm:h-16 bg-orange-300 rounded-full opacity-20'
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                />

                {/* Device Image in Center */}
                <motion.img
                  src='/20250816_1740_Smiling_Girl_with_Smartphone_remix_01k2scgw8red3b2qz70xw9y3x5-removebg-preview.png'
                  alt='SCOREEZE App'
                  className='rounded-2xl sm:rounded-3xl h-[250px] sm:h-[350px] lg:h-[450px] xl:h-[500px] w-auto -z-5'
                  initial={{ scale: 0.9, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 1 }}
                />

                {/* Desktop Stats - Hidden on mobile/tablet */}
                <motion.div
                  className='hidden lg:block absolute top-0 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center gap-2 w-28 sm:w-36'
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1 }}
                >
                  <FaUsers className='text-orange-500 text-2xl sm:text-3xl' />
                  <h3 className='font-bold text-lg sm:text-xl'>10,000+</h3>
                  <p className='text-gray-600 text-center text-xs sm:text-sm'>
                    Students Learned
                  </p>
                </motion.div>

                <motion.div
                  className='hidden lg:block absolute left-0 top-1/2 transform -translate-y-1/2 bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center gap-2 w-28 sm:w-36'
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.2 }}
                >
                  <FaVideo className='text-orange-500 text-2xl sm:text-3xl' />
                  <h3 className='font-bold text-lg sm:text-xl'>500+</h3>
                  <p className='text-gray-600 text-center text-xs sm:text-sm'>Videos</p>
                </motion.div>



                <motion.div
                  className="hidden lg:flex absolute right-0 top-1/2 transform -translate-y-1/2 bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex-col items-center gap-2 w-28 sm:w-36"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.4 }}
                >
                  {/* Icon */}
                  <MdQuiz className="text-orange-500 text-3xl sm:text-4xl" />

                  {/* Count */}
                  <h3 className="font-bold text-lg sm:text-xl">100+</h3>

                  {/* Label */}
                  <p className="text-gray-600 text-center text-xs sm:text-sm">
                    Quizzes
                  </p>
                </motion.div>

                <motion.div
                  className='hidden lg:block absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center gap-2 w-28 sm:w-36'
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.6 }}
                >
                  <FaClipboardCheck className='text-orange-500 text-2xl sm:text-3xl' />
                  <h3 className='font-bold text-lg sm:text-xl'>150+</h3>
                  <p className='text-gray-600 text-center text-xs sm:text-sm'>Assessments</p>
                </motion.div>
              </div>
            </div>

            {/* Right Content - Text */}
            <motion.div
              className='flex-1 text-center lg:text-left space-y-4 sm:space-y-6'
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <h2 className='text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-orange-600 leading-tight'>
                About SCOREEZE
              </h2>
              <p className='text-gray-700 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0'>
                Scoreeze transforms traditional learning into a modern, digital experience. Our platform combines interactive videos, quizzes, revision notes and assessments from expert authors, making education accessible to keen learners.

              </p>
              <p className='text-gray-700 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0'>
                With SCOREEZE, learners can master the subject at their own pace, track progress and revise effectively from any device – mobile, tablet or desktop. We are committed to providing a seamless, high-quality learning experience that empowers each learner to achieve their goal.

              </p>
              {/* <a
                href='#'
                className='inline-block mt-4 px-6 sm:px-8 py-3 sm:py-4 bg-orange-600 text-white font-semibold rounded-xl shadow-lg hover:bg-orange-700 transition'
              >
                Learn More
              </a> */}
            </motion.div>
          </div>
        </div>
      </section>
































      <section id='about' className='bg-gradient-to-tl from-cyan-100 via-white py-20 relative overflow-hidden'>
        <div className='max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center gap-16 relative'>
          {/* Left Content */}
          {/* Left Content - increased width */}
          <div className='flex-[1.5] max-w-lg text-center md:text-left space-y-6'>
            {/* <h2 className='text-4xl md:text-5xl font-extrabold text-orange-600 leading-tight'>
              Learn from <span className='text-orange-700'>18,000+</span>
            </h2> */}

            {/* Hero Paragraph */}
            {/* <p className='text-gray-700 text-base md:text-lg leading-relaxed'>
              Unlock your full potential with Scoreeze! Access high-quality
              videos, quizzes, revision notes, assessments, and eBooks from
              expert authors. Learn at your own pace, revise efficiently, and
              track your progress seamlessly across mobile, tablet, or desktop.
            </p> */}

            {/* Download Section */}
            <div className='flex flex-col sm:flex-col md:justify-start mt-6'>
              <span className='text-gray-500 text-lg font-medium whitespace-nowrap'>
                Download the SCOREEZE App
              </span>
              <div className='flex mt-3 flex-row sm:flex-row sm:items-center gap-4 sm:gap-6 justify-center md:justify-start'>
                <a
                  href='#'
                  aria-label='Get it on Google Play'
                  className='flex items-center gap-2 px-4 py-2 bg-black text-white rounded-sm transition-transform hover:scale-105'
                >
                  <FaGooglePlay className='w-6 h-6' />
                  <span className='text-sm font-medium'>Google Play</span>
                </a>
                <a
                  href='#'
                  aria-label='Download on the App Store'
                  className='flex items-center gap-2 px-4 py-2 bg-black text-white rounded-sm transition-transform hover:scale-105'
                >
                  <FaApple className='w-6 h-6' />
                  <span className='text-sm font-medium'>App Store</span>
                </a>
              </div>
            </div>

            <p className='text-gray-400 text-lg mt-4 max-w-md'>
              Available on Mobile, Tablet, and Desktop
            </p>
          </div>

          {/* Right Content with Blob Background */}
          <div className='flex-1 relative mx-auto h-[450px] md:h-[450px]'>
            {/* Blob SVG 1 */}
            <motion.svg
              className='absolute -top-24 right-0 w-56 h-56 opacity-30'
              viewBox='0 0 600 600'
              xmlns='http://www.w3.org/2000/svg'
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            >
              <path
                fill='#FFA733'
                d='M421.2,328.7Q401,407,328.5,421.2Q256,435.5,197.6,393.8Q139.1,352,115.1,288.9Q91.1,225.8,142.6,170.4Q194,115,261,107.4Q328,99.8,384.6,142.2Q441.3,184.6,421.2,328.7Z'
              />
            </motion.svg>

            {/* Blob SVG 2 */}
            <motion.svg
              className='absolute -bottom-24 left-8 w-60 h-60 opacity-20'
              viewBox='0 0 600 600'
              xmlns='http://www.w3.org/2000/svg'
              animate={{ rotate: [0, -20, 10, 0] }}
              transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
            >
              <path
                fill='#FFB74D'
                d='M429.5,329Q407,408,334,422Q261,436,200,393Q139,350,122,288Q105,226,154,168Q203,110,268,108Q333,106,387,145Q441,184,429.5,329Z'
              />
            </motion.svg>

            {/* Blob SVG 3 - small accent */}
            <motion.svg
              className='absolute top-1/2 left-1/2 w-24 h-24 opacity-15'
              viewBox='0 0 600 600'
              xmlns='http://www.w3.org/2000/svg'
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            >
              <path
                fill='#FFD699'
                d='M300,100Q360,120,380,180Q400,240,340,280Q280,320,220,280Q160,240,180,180Q200,120,300,100Z'
              />
            </motion.svg>

            {/* Device Image */}
            <motion.img
              src='/Aboutimage.png'
              alt='ScoreEze App on Mobile Device'
              className='rounded-3xl h-full w-full object-contain relative z-10'
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
            />
          </div>
        </div>
      </section>




      <section id='why-choose-us' className='bg-gradient-to-tl from-white via-cyan-50 to-white py-20'>
        <div className='max-w-7xl mx-auto px-6 text-center'>
          {/* Section Heading */}
          <motion.h2
            className='text-4xl md:text-5xl font-extrabold text-orange-600 mb-6'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            Why Choose Us
          </motion.h2>
          <motion.p
            className='text-gray-700 text-lg max-w-2xl mx-auto mb-12 leading-relaxed'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            Scoreeze is designed to make learning engaging, efficient, and
            accessible. Here’s why thousands of students trust us:
          </motion.p>

          {/* Feature Cards */}
          <div className='grid md:grid-cols-4 gap-8'>
            {/* Card 1 */}
            <motion.div
              className='flex flex-col items-center bg-white rounded-3xl p-8 shadow-md hover:shadow-xl transition hover:scale-105'
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0 }}
            >
              <FaVideo className='text-orange-500 text-4xl mb-4' />
              <h3 className='text-xl font-semibold mb-2'>Interactive Videos</h3>
              <p className='text-gray-600 text-center'>
                Learn with engaging videos that explain concepts clearly and
                visually.
              </p>
            </motion.div>

            {/* Card 2 */}
            <motion.div
              className='flex flex-col items-center bg-white rounded-3xl p-8 shadow-md hover:shadow-xl transition hover:scale-105'
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <FaBook className='text-orange-500 text-4xl mb-4' />
              <h3 className='text-xl font-semibold mb-2'>
                Comprehensive Notes
              </h3>
              <p className='text-gray-600 text-center'>
                Access revision notes and eBooks from expert authors for all
                subjects.
              </p>
            </motion.div>

            {/* Card 3 */}
            <motion.div
              className='flex flex-col items-center bg-white rounded-3xl p-8 shadow-md hover:shadow-xl transition hover:scale-105'
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <FaClipboardCheck className='text-orange-500 text-4xl mb-4' />
              <h3 className='text-xl font-semibold mb-2'>
                Quizzes & Assessments
              </h3>
              <p className='text-gray-600 text-center'>
                Test your understanding with quizzes, sample papers, and mock
                assessments.
              </p>
            </motion.div>

            {/* Card 4 */}
            <motion.div
              className='flex flex-col items-center bg-white rounded-3xl p-8 shadow-md hover:shadow-xl transition hover:scale-105'
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <FaMobileAlt className='text-orange-500 text-4xl mb-4' />
              <h3 className='text-xl font-semibold mb-2'>Learn Anywhere</h3>
              <p className='text-gray-600 text-center'>
                Study on your mobile, tablet, or desktop at your own
                convenience.
              </p>
            </motion.div>
          </div>
        </div>
      </section>



      <section id='benefits' className='bg-gradient-to-tl from-cyan-50 via-white to-white py-20'>
        <div className='max-w-7xl mx-auto px-6 text-center'>
          {/* Section Heading */}
          <motion.h2
            className='text-4xl md:text-5xl font-extrabold text-orange-600 mb-6'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            Benefits of SCOREEZE
          </motion.h2>
          <motion.p
            className='text-gray-700 text-lg max-w-2xl mx-auto mb-12 leading-relaxed'
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            Tailored benefits for Students, Teachers, and Schools to enhance
            learning and teaching experience.
          </motion.p>

          {/* Benefit Cards */}
          <div className='grid md:grid-cols-3 gap-8'>
            {/* Students Card */}
            <motion.div
              className='bg-white rounded-3xl shadow-lg p-8 flex flex-col gap-4 hover:scale-105 transition'
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0 }}
            >
              <div className='flex items-center gap-3'>
                <FaUserGraduate className='text-orange-500 text-3xl' />
                <h3 className='text-xl font-semibold'>Students</h3>
              </div>
              <ul className='list-disc list-inside text-gray-600 text-left space-y-1'>
                <li>Access interactive videos and eBooks</li>
                <li>Practice with quizzes and assessments</li>
                <li>Track learning progress easily</li>
                <li>Learn anytime, anywhere on any device</li>
              </ul>
            </motion.div>

            {/* Teachers Card */}
            <motion.div
              className='bg-white rounded-3xl shadow-lg p-8 flex flex-col gap-4 hover:scale-105 transition'
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className='flex items-center gap-3'>
                <FaChalkboardTeacher className='text-orange-500 text-3xl' />
                <h3 className='text-xl font-semibold'>Teachers</h3>
              </div>
              <ul className='list-disc list-inside text-gray-600 text-left space-y-1'>
                <li>Create assessments and track student performance</li>
                <li>Provide personalized guidance</li>
                <li>Access teaching resources and notes</li>
                <li>Monitor class performance efficiently</li>
              </ul>
            </motion.div>

            {/* Schools Card */}
            <motion.div
              className='bg-white rounded-3xl shadow-lg p-8 flex flex-col gap-4 hover:scale-105 transition'
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className='flex items-center gap-3'>
                <FaSchool className='text-orange-500 text-3xl' />
                <h3 className='text-xl font-semibold'>Schools</h3>
              </div>
              <ul className='list-disc list-inside text-gray-600 text-left space-y-1'>
                <li>Integrate digital learning across classrooms</li>
                <li>Track overall student performance</li>
                <li>Enhance educational quality and efficiency</li>
                <li>Easy adoption for teachers and students</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>



      <section id="testimonials" className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-start gap-8">
          {/* Left Side - Heading */}
          <div className="md:w-1/3 text-left">
            <h2 className="text-4xl md:text-5xl font-extrabold text-orange-600 mb-4">
              What Our Students Say
            </h2>
            <p className="text-gray-700 text-lg">
              Real feedback from students using SCOREEZE for learning from 6th to 10th grade.

            </p>
          </div>

          {/* Right Side - Carousel */}
          <div className="md:w-2/3 relative flex items-center justify-center gap-4">
            {/* Prev Button */}
            <button
              onClick={prev}
              className="absolute left-0 bg-orange-600 text-white rounded-full p-3 hover:bg-orange-700 transition z-10"
            >
              &#8592;
            </button>

            {/* Cards */}
            {visibleTestimonials.map((testimonial) => (
              <motion.div
                key={testimonial.name}
                className="bg-white rounded-3xl shadow-lg p-8 flex flex-col items-center gap-4 w-80"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-orange-600"
                />
                <h3 className="text-xl font-semibold">{testimonial.name}</h3>
                <p className="text-gray-700 text-center">{testimonial.text}</p>
                <div className="flex gap-1 mt-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <FaStar
                      key={i}
                      className={`${i < testimonial.rating
                        ? "text-orange-500"
                        : "text-gray-300"
                        }`}
                    />
                  ))}
                </div>
              </motion.div>
            ))}

            {/* Next Button */}
            <button
              onClick={next}
              className="absolute right-0 bg-orange-600 text-white rounded-full p-3 hover:bg-orange-700 transition z-10"
            >
              &#8594;
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='bg-gray-800 border-t border-gray-700 relative overflow-hidden'>
        {/* Background Pattern */}
        <div className='absolute inset-0 opacity-5'>
          <div className='absolute top-10 left-10 w-32 h-32 bg-orange-400 rounded-full blur-3xl'></div>
          <div className='absolute bottom-10 right-10 w-40 h-40 bg-cyan-400 rounded-full blur-3xl'></div>
          <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-purple-400 rounded-full blur-3xl'></div>
        </div>

        <div className='max-w-7xl mx-auto px-6 py-12 relative'>
          {/* Main Footer Content */}
          <div className='grid grid-cols-1 md:grid-cols-12 lg:grid-cols-12 gap-8 mb-8'>
            {/* Brand Section */}
            <div className='md:col-span-4 lg:col-span-3 space-y-4'>
              <div className='flex items-center space-x-3'>
                <Image
                  src='/ScoreEZE_Logo.png'
                  alt='SCOREEZE Logo'
                  width={120}
                  height={40}
                  className='h-12 w-auto'
                />
              </div>
              <p className='text-gray-300 text-sm leading-relaxed'>
                Empowering learners with immersive activities through videos, quizzes and comprehensive reading materials.
              </p>
              <div className='flex space-x-3 pt-2'>
                <a href='https://facebook.com/scoreeze' target='_blank' rel='noopener noreferrer'
                  className='w-11 h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-110'>
                  <FaFacebookF className='text-sm' />
                </a>
                <a href='https://twitter.com/scoreeze' target='_blank' rel='noopener noreferrer'
                  className='w-11 h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-110'>
                  <FaSquareXTwitter />
                </a>
                <a href='https://instagram.com/scoreeze' target='_blank' rel='noopener noreferrer'
                  className='w-11 h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-110'>
                  <FaInstagram className='text-sm' />
                </a>
                <a href='https://linkedin.com/company/scoreeze' target='_blank' rel='noopener noreferrer'
                  className='w-11 h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-110'>
                  <FaLinkedinIn className='text-sm' />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className='md:col-span-2 lg:col-span-2 space-y-4'>
              <h4 className='text-sm font-bold text-white uppercase tracking-wider border-b-2 border-orange-400 pb-1 w-fit'>Quick Links</h4>
              <ul className='space-y-3'>
                <li>
                  <button onClick={() => scrollToSection('about-scoreeze')}
                    className='text-gray-300 hover:text-orange-400 text-sm transition-all duration-300 hover:translate-x-1 font-medium'>
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('courses')}
                    className='text-gray-300 hover:text-orange-400 text-sm transition-all duration-300 hover:translate-x-1 font-medium'>
                    Courses
                  </button>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div className='md:col-span-2 lg:col-span-2 space-y-4'>
              <h4 className='text-sm font-bold text-white uppercase tracking-wider border-b-2 border-cyan-400 pb-1 w-fit'>Support</h4>
              <ul className='space-y-3'>
                <li>
                  <a href='/privacy-policy'
                    className='text-gray-300 hover:text-cyan-400 text-sm transition-all duration-300 hover:translate-x-1 font-medium'>
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href='/terms-of-service'
                    className='text-gray-300 hover:text-cyan-400 text-sm transition-all duration-300 hover:translate-x-1 font-medium'>
                    Terms & Conditions
                  </a>
                </li>
              </ul>
            </div>

            {/* App Download */}
            <div className='md:col-span-4 lg:col-span-3 space-y-4'>
              <h4 className='text-sm font-bold text-white uppercase tracking-wider border-b-2 border-purple-400 pb-1 w-fit'>Download App</h4>
              <p className='text-gray-300 text-sm mb-4 font-medium'>Get the SCOREEZE app for the best learning experience</p>
              <div className='flex flex-row gap-2 sm:gap-3'>
                <a href='https://play.google.com/store/apps/details?id=com.scoreeze' target='_blank' rel='noopener noreferrer'
                  className='flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-xl transition-all duration-300 gap-3 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105'>
                  <FaGooglePlay className='text-lg' />
                  <span>Google Play</span>
                </a>
                <a href='https://apps.apple.com/app/scoreeze/id123456789' target='_blank' rel='noopener noreferrer'
                  className='flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-xl transition-all duration-300 gap-3 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105'>
                  <FaApple className='text-lg' />
                  <span>App Store</span>
                </a>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className='border-t border-slate-300 my-8 relative'>
            <div className='absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400 to-transparent'></div>
          </div>

          {/* Bottom Section */}
          <div className='flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0'>
            <div className='text-gray-400 text-sm font-medium'>
              © {new Date().getFullYear()} SCOREEZE. All rights reserved.
            </div>
            <div className='flex items-center space-x-2 text-sm text-gray-400 font-medium'>
              <span>Made with</span>
              <span className='text-red-500 animate-pulse'>❤️</span>
              <span>for better education</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
